import React, { useState } from 'react'
import {
  Box,
  Flex,
  Heading,
  Text,
  Radio,
  RadioGroup,
  Stack,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import { useSettings } from '../contexts/SettingsContext'

type PlaybackMode = 'manual' | 'auto'

const SettingsManager = () => {
  const { settings, loading, error, updatePlaybackMode } = useSettings()
  const [success, setSuccess] = useState('')

  const handleModeChange = async (mode: PlaybackMode) => {
    const result = await updatePlaybackMode(mode)
    if (result.success) {
      setSuccess('설정이 저장되었습니다!')
      setTimeout(() => setSuccess(''), 3000)
    }
  }

  return (
    <Box p={{ base: 4, sm: 8 }}>
      <Heading as="h2" size="lg" color="gray.900" mb={8}>
        재생 설정
      </Heading>

      {success && (
        <Alert status="success" borderRadius="lg" mb={6}>
          <AlertIcon />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        p={6}
      >
        <Heading as="h3" size="md" color="gray.900" mb={4}>
          재생 모드
        </Heading>
        <Text color="gray.600" mb={6}>
          링크로 접속했을 때 영상이 어떻게 재생될지 선택하세요.
        </Text>

        <RadioGroup
          value={settings.playbackMode}
          onChange={(val) => handleModeChange(val as PlaybackMode)}
        >
          <Stack spacing={4}>
            <Flex alignItems="flex-start">
              <Radio
                id="manual-mode"
                value="manual"
                colorScheme="blue"
                isDisabled={loading}
                mt={1}
              />
              <Box ml={3}>
                <Text
                  as="label"
                  htmlFor="manual-mode"
                  fontWeight="medium"
                  color="gray.900"
                  cursor="pointer"
                >
                  수동 재생 모드
                </Text>
                <Text fontSize="sm" color="gray.500">
                  링크 접속 → 재생 버튼 표시 → 클릭 → 영상 재생
                </Text>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  사용자가 직접 재생 버튼을 눌러야 재생됩니다.
                </Text>
              </Box>
            </Flex>

            <Flex alignItems="flex-start">
              <Radio
                id="auto-mode"
                value="auto"
                colorScheme="blue"
                isDisabled={loading}
                mt={1}
              />
              <Box ml={3}>
                <Text
                  as="label"
                  htmlFor="auto-mode"
                  fontWeight="medium"
                  color="gray.900"
                  cursor="pointer"
                >
                  자동 재생 모드
                </Text>
                <Text fontSize="sm" color="gray.500">
                  링크 접속 → 바로 영상 재생 (음소거) → 사용자 클릭 → 소리 켜짐
                </Text>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  링크 접속 시 바로 재생되지만, 소리를 들으려면 한 번 더
                  클릭해야 합니다.
                </Text>
              </Box>
            </Flex>
          </Stack>
        </RadioGroup>

        <Box mt={6} p={4} bg="gray.50" borderRadius="lg">
          <Flex alignItems="center">
            <Box w={2} h={2} bg="green.500" borderRadius="full" mr={3} />
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              현재 설정:{' '}
              {settings.playbackMode === 'manual'
                ? '수동 재생 모드'
                : '자동 재생 모드'}
            </Text>
          </Flex>
        </Box>
      </Box>

      <Box
        mt={8}
        bg="blue.50"
        border="1px solid"
        borderColor="blue.200"
        borderRadius="lg"
        p={6}
      >
        <Heading as="h4" size="sm" color="blue.900" mb={2}>
          미리보기
        </Heading>
        <Text fontSize="sm" color="blue.700">
          {settings.playbackMode === 'manual'
            ? '사용자가 링크에 접속하면 재생 버튼이 표시되고, 클릭해야 영상이 재생됩니다.'
            : '사용자가 링크에 접속하면 바로 영상이 재생됩니다 (음소거 상태).'}
        </Text>
      </Box>

      <Box
        mt={8}
        bg="amber.50"
        border="1px solid"
        borderColor="amber.200"
        borderRadius="lg"
        p={6}
      >
        <Heading as="h4" size="sm" color="amber.900" mb={3}>
          재생 설정이 적용되는 방식
        </Heading>
        <Text fontSize="sm" color="amber.800" mb={3}>
          위에서 선택한 재생 모드는 <strong>메인 페이지</strong>에서 비디오를
          클릭했을 때 열리는 재생 창에 자동으로 적용됩니다.
        </Text>
        <Box
          as="ul"
          fontSize="sm"
          color="amber.800"
          pl={4}
          mb={3}
          sx={{ '& li': { mb: 2 } }}
        >
          <li>
            <strong>수동 모드</strong>: 사용자가 재생 버튼을 눌러야 영상이
            재생됩니다.
          </li>
          <li>
            <strong>자동 모드</strong>: 창이 열리면 바로 재생됩니다 (음소거).
            사용자가 영상을 클릭하면 소리가 켜집니다.
          </li>
        </Box>
        <Text fontSize="sm" color="amber.700">
          비디오 관리에서 등록한 YouTube URL은 그대로 사용됩니다. 재생 설정은
          메인 페이지에서 해당 비디오를 재생할 때만 적용됩니다.
        </Text>
      </Box>
    </Box>
  )
}

export default SettingsManager
